/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { DateInput, Divider, Form, Grid, Tooltip } from '@mairistem/composants';

import { OPTIONS_ETAT_MATRIMONIAL } from '@/constants';
import {
  validationComplementInsee,
  validationNombreEnfantsMax,
} from '@/utils/validations';

import PanelModalEdition from '../../PanelModalEdition';

interface AutresInformationsModalProps {
  protectionJuridique?: boolean | null;
  protectionOfpra?: boolean | null;
  identiteVerifiee?: boolean | null;
  etatMatrimonialCode?: string | null;
  dateDivorce?: string | null;
  dateVeuvage?: string | null;
  dejaPacse?: string | null;
  nombreEnfants?: number | null;
  showAllInsee?: boolean;
  hideInseeFields?: boolean;
  inseeObligatoire?: boolean;
  radioGroupSuffix?: string;
  onSave: () => Promise<void>;
  onChange: (data: {
    protectionJuridique?: boolean | null;
    protectionOfpra?: boolean | null;
    identiteVerifiee?: boolean | null;
    etatMatrimonialCode?: string | null;
    dateDivorce?: string | null;
    dateVeuvage?: string | null;
    dejaPacse?: string | null;
    nombreEnfants?: number | null;
  }) => void;
  onClose: () => void;
  onCancel: () => void;
}

const AutresInformationsModal = (props: AutresInformationsModalProps) => {
  const {
    protectionJuridique,
    protectionOfpra,
    identiteVerifiee,
    etatMatrimonialCode,
    dateDivorce,
    dateVeuvage,
    dejaPacse,
    nombreEnfants,
    showAllInsee = false,
    hideInseeFields = false,
    inseeObligatoire = false,
    radioGroupSuffix = 'autres-informations',
    onSave,
    onChange,
    onClose,
    onCancel,
  } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Record<string, string | boolean> & { api?: boolean }
  >({});

  const handleChangeProtectionJuridique = React.useCallback(
    (value: boolean) => {
      onChange({ protectionJuridique: value });
    },
    [onChange],
  );

  const handleChangeProtectionOfpra = React.useCallback(
    (value: boolean) => {
      onChange({ protectionOfpra: value });
    },
    [onChange],
  );

  const handleChangeIdentiteVerifiee = React.useCallback(
    (value: boolean) => {
      onChange({ identiteVerifiee: value });
    },
    [onChange],
  );

  const handleChangeEtatMatrimonialCode = React.useCallback(
    (_e: React.SyntheticEvent, data: { value: string }) => {
      onChange({
        etatMatrimonialCode: data.value,
        // La date n'a de sens que pour l'état correspondant : on efface
        // l'autre pour ne pas conserver une valeur devenue incohérente.
        ...(data.value !== 'D' && { dateDivorce: null }),
        ...(data.value !== 'V' && { dateVeuvage: null }),
      });
    },
    [onChange],
  );

  const handleChangeDateDivorce = React.useCallback(
    (date: Date | null) => {
      onChange({ dateDivorce: date ? dayjs(date).format('YYYY-MM-DD') : null });
    },
    [onChange],
  );

  const handleChangeDateVeuvage = React.useCallback(
    (date: Date | null) => {
      onChange({ dateVeuvage: date ? dayjs(date).format('YYYY-MM-DD') : null });
    },
    [onChange],
  );

  const handleChangeDejaPacse = React.useCallback(
    (value: string) => {
      onChange({ dejaPacse: value });
    },
    [onChange],
  );

  const handleChangeNombreEnfants = React.useCallback(
    (_e: React.FormEvent<HTMLInputElement>, data: { value: string }) => {
      onChange({
        nombreEnfants: data.value === '' ? null : Number(data.value),
      });
    },
    [onChange],
  );

  // Erreur immédiate au-delà du plafond, sans attendre l'enregistrement.
  const erreurNombreEnfants =
    (showAllInsee &&
      !hideInseeFields &&
      validationNombreEnfantsMax(nombreEnfants)) ||
    (errors.nombreEnfants as string | undefined);

  const handleSave = React.useCallback(() => {
    const nombreEnfantsMax =
      showAllInsee && !hideInseeFields
        ? validationNombreEnfantsMax(nombreEnfants)
        : null;

    const inseeErrors =
      inseeObligatoire && !hideInseeFields
        ? validationComplementInsee(
            {
              etatMatrimonialCode,
              dateDivorce,
              dateVeuvage,
              dejaPacse,
              nombreEnfants,
            },
            showAllInsee,
          )
        : {
            ...(nombreEnfantsMax && { nombreEnfants: nombreEnfantsMax }),
          };

    setErrors(inseeErrors);
    if (!_.isEmpty(inseeErrors)) return;

    setLoading(true);

    onSave()
      .then(() => {
        onClose();
      })
      .catch(() => {
        setErrors({ api: true });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    dateDivorce,
    dateVeuvage,
    dejaPacse,
    etatMatrimonialCode,
    hideInseeFields,
    inseeObligatoire,
    nombreEnfants,
    onClose,
    onSave,
    showAllInsee,
  ]);

  return (
    <PanelModalEdition
      data-testid="autres-informations-modal"
      errors={errors}
      loading={loading}
      header="AUTRES INFORMATIONS"
      onSave={handleSave}
      onClose={onCancel}
    >
      <Grid as={Form}>
        {/* Protection juridique : même ordre que l'étape "Partenaire" de l'assistant */}
        <Grid.Row>
          <Grid.Column
            width={16}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.5em',
            }}
          >
            <label style={{ margin: 0 }}>
              <b>
                Placé(e) sous la protection juridique et administrative de
                l&apos;OFPRA :
              </b>
            </label>
            <Form.Group inline style={{ margin: 0 }}>
              {/* @ts-ignore */}
              <Form.Checkbox
                radio
                label="Oui"
                checked={protectionOfpra === true}
                data-testid="autres-informations-protection-ofpra-oui"
                onChange={() => handleChangeProtectionOfpra(true)}
              />
              {/* @ts-ignore */}
              <Form.Checkbox
                radio
                label="Non"
                checked={protectionOfpra === false}
                data-testid="autres-informations-protection-ofpra-non"
                onChange={() => handleChangeProtectionOfpra(false)}
              />
            </Form.Group>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column
            width={9}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.5em',
            }}
          >
            <label style={{ margin: 0 }}>
              <b>
                Fait l&apos;objet d&apos;une mesure de protection juridique :
              </b>
            </label>
            <Form.Group inline style={{ margin: 0 }}>
              {/* @ts-ignore */}
              <Form.Checkbox
                radio
                label="Oui"
                checked={protectionJuridique === true}
                data-testid="autres-informations-protection-juridique-oui"
                onChange={() => handleChangeProtectionJuridique(true)}
              />
              {/* @ts-ignore */}
              <Form.Checkbox
                radio
                label="Non"
                checked={protectionJuridique === false}
                data-testid="autres-informations-protection-juridique-non"
                onChange={() => handleChangeProtectionJuridique(false)}
              />
            </Form.Group>
          </Grid.Column>
          <Grid.Column
            width={7}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.5em',
            }}
          >
            <label style={{ margin: 0 }}>
              <b>A fourni un titre d&apos;identité vérifié :</b>
            </label>
            <Form.Group inline style={{ margin: 0 }}>
              {/* @ts-ignore */}
              <Form.Checkbox
                radio
                label="Oui"
                checked={identiteVerifiee === true}
                data-testid="autres-informations-identite-verifiee-oui"
                onChange={() => handleChangeIdentiteVerifiee(true)}
              />
              {/* @ts-ignore */}
              <Form.Checkbox
                radio
                label="Non"
                checked={identiteVerifiee === false}
                data-testid="autres-informations-identite-verifiee-non"
                onChange={() => handleChangeIdentiteVerifiee(false)}
              />
            </Form.Group>
          </Grid.Column>
        </Grid.Row>
        {!hideInseeFields && (
          <>
            <Divider />
            <Grid.Row>
              <Grid.Column
                width={16}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.5em',
                }}
              >
                <label style={{ margin: 0 }}>
                  <b>Etat matrimonial antérieur (INSEE) :</b>
                </label>
                <Form.Group inline style={{ margin: 0 }}>
                  {OPTIONS_ETAT_MATRIMONIAL.map((option) => (
                    <Form.Radio
                      key={option.value}
                      data-testid={`autres-informations-etat-matrimonial-${option.value}`}
                      name={`etat-matrimonial-${radioGroupSuffix}`}
                      label={option.text}
                      value={option.value}
                      checked={etatMatrimonialCode === option.value}
                      error={!!errors.etatMatrimonial}
                      onClick={handleChangeEtatMatrimonialCode}
                    />
                  ))}
                </Form.Group>
              </Grid.Column>
            </Grid.Row>
            {(etatMatrimonialCode === 'V' || etatMatrimonialCode === 'D') && (
              <Grid.Row style={{ paddingTop: 0 }}>
                <Grid.Column
                  width={16}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.5em',
                  }}
                >
                  <label style={{ margin: 0 }}>
                    <b>
                      {etatMatrimonialCode === 'V'
                        ? 'Date du veuvage (INSEE) :'
                        : 'Date du divorce (INSEE) :'}
                    </b>
                  </label>
                  <DateInput
                    data-testid={
                      etatMatrimonialCode === 'V'
                        ? 'autres-informations-date-veuvage'
                        : 'autres-informations-date-divorce'
                    }
                    error={!!errors.dateEtatMatrimonial}
                    value={
                      etatMatrimonialCode === 'V'
                        ? dateVeuvage
                          ? new Date(dateVeuvage)
                          : null
                        : dateDivorce
                          ? new Date(dateDivorce)
                          : null
                    }
                    onChange={
                      etatMatrimonialCode === 'V'
                        ? handleChangeDateVeuvage
                        : handleChangeDateDivorce
                    }
                  />
                </Grid.Column>
              </Grid.Row>
            )}
            <Grid.Row style={{ paddingTop: 0 }}>
              <Grid.Column
                width={16}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.5em',
                }}
              >
                <label style={{ margin: 0 }}>
                  <b>Partenaire déjà pacsé par le passé (INSEE) :</b>
                </label>
                <Form.Group inline style={{ margin: 0 }}>
                  {/* @ts-ignore */}
                  <Form.Checkbox
                    radio
                    label="Oui"
                    checked={dejaPacse === 'Oui'}
                    data-testid="autres-informations-deja-pacse-oui"
                    onChange={() => handleChangeDejaPacse('Oui')}
                  />
                  {/* @ts-ignore */}
                  <Form.Checkbox
                    radio
                    label="Non"
                    checked={dejaPacse === 'Non'}
                    error={!!errors.dejaPacse}
                    data-testid="autres-informations-deja-pacse-non"
                    onChange={() => handleChangeDejaPacse('Non')}
                  />
                </Form.Group>
              </Grid.Column>
            </Grid.Row>
            {showAllInsee && (
              <Grid.Row style={{ paddingTop: 0 }}>
                <Grid.Column
                  width={16}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.5em',
                  }}
                >
                  <label style={{ margin: 0 }}>
                    <b>
                      Nombre d&apos;enfants en commun avec le partenaire 2
                      (INSEE) :
                    </b>
                  </label>
                  <Tooltip
                    trigger={
                      <Form.Input
                        style={{ width: 100 }}
                        value={nombreEnfants ?? ''}
                        error={!!erreurNombreEnfants}
                        input={{
                          'data-testid': 'autres-informations-nombre-enfants',
                        }}
                        onChange={handleChangeNombreEnfants}
                      />
                    }
                    type={erreurNombreEnfants ? 'error' : ''}
                    message={erreurNombreEnfants}
                  />
                </Grid.Column>
              </Grid.Row>
            )}
          </>
        )}
      </Grid>
    </PanelModalEdition>
  );
};

export default AutresInformationsModal;
